// Generated by GitHub Copilot
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { ApiError } from '#core/api/Api.ts'
import { State, useComponentState } from '#core/state/State.ts'
import CompanyUsersApi from '#core/api/auth/CompanyUsersApi.ts'
import CostRatesApi from '#core/api/operations/CostRatesApi.ts'
import PriceRatesApi from '#core/api/operations/PriceRatesApi.ts'
import UserCreate from '#core/entities/auth/users/UserCreate.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { CURRENCY_VALUES } from '#core/entities/operations/parts/Part.ts'
import { isValidCostRate } from '#core/entities/operations/settings/CostRate.ts'
import { useUserState } from '#core/state/UserState.ts'
import { showToast } from '#core/notifications/toast.ts'
import { CardModal } from '#components/primitives/components/card-modal.tsx'
import { FormFooterActions } from '#components/primitives/components/form-footer-actions.tsx'
import { FormField } from '#components/primitives/forms/form-field.tsx'
import { UserFields } from '#components/domain/auth/user/shared/user-fields.tsx'
import { MemberSignInRequirements } from '#components/entities/users/requirements/member-sign-in-requirements.tsx'
import { useSsoProviders } from '#components/entities/users/hooks/use-sso-providers.ts'
import { CostRateFields } from '#components/domain/operations/settings/shared/cost-rate-fields.tsx'
import {
  RoleAssignmentFields,
  type AssignmentSelection,
  emptyAssignment,
  assignmentIsValid,
  assignmentPayload,
  selectionFromMember,
} from '#components/domain/auth/auth/shared/role-assignment-fields.tsx'
import {
  PermissionErrorAlert,
  permissionErrorBody,
  type PermissionErrorBody,
} from '#components/domain/auth/auth/shared/permission-error.tsx'

const usersApi = new CompanyUsersApi()
const costRatesApi = new CostRatesApi()

interface UserEditModalProps {
  isOpen: boolean
  userId: string | null
  roles: RoleDetail[]
  onClose: () => void
  onSaved: () => void
}

/** Inline state for the user create/edit modal. */
class UserEditState extends State<UserEditState> {
  firstName = ''
  lastName = ''
  email = ''
  password = ''
  /** Role XOR personal-permissions selection (the membership's single authz source). */
  assignment: AssignmentSelection = emptyAssignment()
  saving = false
  mfaRequired = false
  ssoRequired = false
  _originalMfaRequired = false
  _originalSsoRequired = false
  /** Set once the backend refused to switch the SSO requirement on: no tenant is connected. */
  ssoUnavailable = false
  /** Structured permission-feature-flags 422 (disallowed permission / cap), rendered inline. */
  permError: PermissionErrorBody | null = null
  /** The user's own hourly rate as typed; blank falls back to the company default. */
  costRate = ''
  _originalCostRate = ''
  /** The currency of the user's own rate; preselected from their stored rate, else the company default. */
  costCurrency = ''
  _originalCostCurrency = ''
  /** The user the latest {@link loadCostRates} call is for, so a late response for another is dropped. */
  _costRatesUserId: string | null = null
  /** The company default rate as display text, shown as the placeholder the user falls back to. */
  defaultCostRatePlaceholder = ''

  /** Whether the typed rate is neither blank nor a rate the backend accepts, so Save can't store it. */
  get costRateInvalid(): boolean {
    const typed = this.costRate.trim()
    return typed !== '' && !isValidCostRate(Number(typed))
  }

  /** Whether a rate is typed but no currency is picked for it, so Save can't store it. */
  get costCurrencyMissing(): boolean {
    return this.costRate.trim() !== '' && !CURRENCY_VALUES.some((value) => value === this.costCurrency)
  }

  /** Resets form fields. */
  reset(): void {
    this.reactTo.firstName = ''
    this.reactTo.lastName = ''
    this.reactTo.email = ''
    this.reactTo.password = ''
    this.reactTo.assignment = emptyAssignment()
    this.reactTo.saving = false
    this.reactTo.permError = null
    this.reactTo.ssoUnavailable = false
    this.reactTo.mfaRequired = this._originalMfaRequired = false
    this.reactTo.ssoRequired = this._originalSsoRequired = false
    this.reactTo.costRate = ''
    this._originalCostRate = ''
    this.reactTo.costCurrency = ''
    this._originalCostCurrency = ''
  }

  /** Populates form from an existing user fetched by ID. */
  async loadUser(id: string): Promise<void> {
    const user = await usersApi.getUser(id)
    if (!user) return
    this.reactTo.firstName = user.first_name ?? ''
    this.reactTo.lastName = user.last_name ?? ''
    this.reactTo.email = user.email ?? ''
    this.reactTo.assignment = selectionFromMember(user.role_id ?? null, user.personal_permissions ?? [])
    this.reactTo.mfaRequired = this._originalMfaRequired = user.mfa_required
    this.reactTo.ssoRequired = this._originalSsoRequired = user.sso_required
  }

  /**
   * Loads the company default rate and, when editing, the user's own rate. Best-effort: on
   * failure the field stays empty, and a response that lands after the modal moved to another
   * user is dropped. A rate or currency entered before the response keeps what was entered.
   */
  async loadCostRates(userId: string | null): Promise<void> {
    this._costRatesUserId = userId
    try {
      const [defaultRate, userRate, rates] = await Promise.all([
        costRatesApi.getDefaultRate(),
        userId ? costRatesApi.getUserRate(userId) : null,
        new PriceRatesApi().getRates(),
      ])
      if (this._costRatesUserId !== userId) return
      this.reactTo.defaultCostRatePlaceholder = defaultRate?.formatted(rates)?.display ?? ''
      const costRate = (userRate?.price_currency && userRate.price_points?.amountIn(rates.tables, userRate.price_currency)?.toString()) || ''
      if (this.costRate === this._originalCostRate) this.reactTo.costRate = costRate
      this._originalCostRate = costRate
      const costCurrency = userRate?.price_currency?.toUpperCase() ?? ''
      if (!this.costCurrency) this.reactTo.costCurrency = costCurrency || (defaultRate?.price_currency?.toUpperCase() ?? '')
      this._originalCostCurrency = costCurrency
    } catch {
      // non-critical
    }
  }

  async save(userId: string | null, onSaved: () => void, onClose: () => void): Promise<void> {
    this.reactTo.saving = true
    this.reactTo.permError = null
    const payload = assignmentPayload(this.assignment)
    let savedId = userId
    let close = true
    try {
      if (userId) {
        await usersApi.updateUser(userId, {
          first_name: this.firstName || undefined,
          last_name: this.lastName || undefined,
          email: this.email || undefined,
        })
        if (payload) {
          await usersApi.updateAssignment(userId, payload.role_id, payload.personal_permissions)
        }
        close = await this.saveRequirements(userId)
      } else {
        const data = new UserCreate()
        data.email = this.email
        data.password = this.password
        data.first_name = this.firstName || null
        data.last_name = this.lastName || null
        if (payload) {
          data.role_id = payload.role_id
          data.personal_permissions = payload.personal_permissions
        }
        const created = await usersApi.createUser(data)
        savedId = created?.id ?? null
      }
      if (savedId) await this.saveCostRate(savedId)
      onSaved()
      if (close) onClose()
    } catch (e) {
      // A permission-feature-flags 422 (disallowed permission / cap exceeded) carries a
      // machine-readable body we render inline; anything else keeps the generic toast.
      const permError = permissionErrorBody(e)
      if (permError) {
        this.reactTo.permError = permError
      } else {
        showToast(i18n.t('usersTeams.saveError', { ns: 'settings' }), 'danger')
      }
    } finally {
      this.reactTo.saving = false
    }
  }

  /**
   * Saves changed sign-in requirements for the already-saved user; a failure is toasted without undoing the
   * user save. Resolves false when switching the SSO requirement on was refused for want of a connected tenant:
   * the toggle is switched back and the modal stays open to say so.
   */
  private async saveRequirements(userId: string): Promise<boolean> {
    const mfa = this.mfaRequired !== this._originalMfaRequired ? this.mfaRequired : undefined
    const sso = this.ssoRequired !== this._originalSsoRequired ? this.ssoRequired : undefined
    if (mfa === undefined && sso === undefined) return true
    try {
      await usersApi.updateRequirements(userId, mfa, sso)
    } catch (e) {
      if (sso === true && e instanceof ApiError && e.response?.status === 422) {
        this.reactTo.ssoRequired = this._originalSsoRequired
        this.reactTo.ssoUnavailable = true
        return false
      }
      showToast(i18n.t('signInRequirements.member.saveError', { ns: 'settings' }), 'danger')
    }
    return true
  }

  /** Saves a changed rate for the already-saved user; a failure is toasted without undoing the user save. */
  private async saveCostRate(userId: string): Promise<void> {
    const typed = this.costRate.trim()
    const currency = CURRENCY_VALUES.find((value) => value === this.costCurrency)
    if (typed === this._originalCostRate && (!typed || currency === this._originalCostCurrency)) return
    try {
      if (!typed) await costRatesApi.deleteUserRate(userId)
      else if (currency) await costRatesApi.setUserRate(userId, Number(typed), currency)
    } catch {
      showToast(i18n.t('usersTeams.userModal.costRateSaveError', { ns: 'settings' }), 'danger')
    }
  }
}

/** Modal for creating or editing a company user (profile + role/custom-permissions assignment). */
export function UserEditModal({ isOpen, userId, roles, onClose, onSaved }: UserEditModalProps) {
  const { t } = useTranslation('settings')
  const state = useComponentState(UserEditState)
  const userState = useUserState()
  const ssoProviders = useSsoProviders()
  const canViewCosts = userState.hasPermission(PermissionService.COSTS, PermissionAction.READ)
  const canEditCosts = userState.hasPermission(PermissionService.COSTS, PermissionAction.UPDATE)
  const isEdit = userId != null

  useEffect(() => {
    if (isOpen) {
      state.reset()
      if (userId) void state.loadUser(userId)
      if (canViewCosts) void state.loadCostRates(userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId])

  const title = isEdit ? t('usersTeams.userModal.editTitle') : t('shared.fields.addUser')
  const isPasswordMissing = !isEdit && !state.password?.trim()
  const isSubmitDisabled = state.saving || !state.email || isPasswordMissing || !assignmentIsValid(state.assignment) || state.costRateInvalid || state.costCurrencyMissing

  const footer = (
    <div className="d-flex flex-column align-items-end gap-2">
      {isPasswordMissing ? (
        <div className="text-danger">
          {i18n.t('settings:usersTeams.userModal.passwordRequired', { defaultValue: 'Password is required' })}
        </div>
      ) : null}
      <FormFooterActions
        onCancel={onClose}
        onSubmit={() => {
          if (isPasswordMissing) {
            showToast(i18n.t('settings:usersTeams.userModal.passwordRequired', { defaultValue: 'Password is required' }), 'danger')
            return
          }
          void state.save(userId, onSaved, onClose)
        }}
        submitting={state.saving}
        submitDisabled={isSubmitDisabled}
        submitLabel={isEdit ? t('shared.actions.save') : t('shared.fields.createUser')}
        submittingLabel={isEdit ? t('shared.actions.saving') : t('shared.actions.creating')}
      />
    </div>
  )

  return (
    <CardModal isOpen={isOpen} title={title} footer={footer} onClose={onClose}>
      {/* Shared create-user body (email + name grid + password); password only in create mode. */}
      <UserFields
        idPrefix="user-edit"
        emailLabel={t('shared.fields.emailAddress')}
        email={state.email}
        onEmailChange={(v) => { state.email = v }}
        firstName={state.firstName}
        onFirstNameChange={(v) => { state.firstName = v }}
        lastName={state.lastName}
        onLastNameChange={(v) => { state.lastName = v }}
        password={isEdit ? undefined : state.password}
        onPasswordChange={(v) => { state.password = v }}
      />

      {/* Role XOR custom permissions (shared assignment control). */}
      <RoleAssignmentFields
        roles={roles}
        value={state.assignment}
        onChange={(next) => { state.reactTo.assignment = next }}
        idPrefix="user-edit"
        disabled={state.saving}
      />

      {state.permError && <div className="mt-3"><PermissionErrorAlert body={state.permError} /></div>}

      {canViewCosts && (
        <FormField
          label={t('usersTeams.userModal.costRate')}
          className="mt-3"
          invalid={state.costRateInvalid || state.costCurrencyMissing}
          showFeedback={false}
        >
          <CostRateFields
            amount={state.costRate}
            ariaLabel={t('usersTeams.userModal.costRate')}
            amountPlaceholder={state.defaultCostRatePlaceholder}
            currency={state.costCurrency}
            disabled={state.saving || !canEditCosts}
            onAmountChange={(value) => { state.costRate = value }}
            onCurrencyChange={(currency) => { state.costCurrency = currency }}
          />
        </FormField>
      )}

      {isEdit && (
        <MemberSignInRequirements
          mfaRequired={state.mfaRequired}
          ssoRequired={state.ssoRequired}
          showSso={ssoProviders.hasSsoProvider || state._originalSsoRequired}
          disabled={state.saving}
          ssoUnavailable={state.ssoUnavailable}
          onMfaRequiredChange={(required) => { state.mfaRequired = required }}
          onSsoRequiredChange={(required) => { state.ssoRequired = required }}
        />
      )}
    </CardModal>
  )
}
